/**
 * settings/SettingsTab.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Profile update and account management screen.
 *
 * This file contains the main SettingsTab component (~750 lines).
 * Small helper modals are extracted to settingsComponents.jsx.
 *
 * FEATURES:
 *   - Profile picture upload with live preview
 *   - Edit personal info: name, phone, location, business name
 *   - Business logo upload
 *   - ID verification: front + back document upload
 *   - Verification status display with admin snapshot diff
 *   - Password reset flow
 *   - Member since date
 *   - Logout button
 *
 * PROPS (from ProfilePage + directly passed):
 *   user               — current user from AuthContext
 *   profileImage       — current profile image (URL or base64)
 *   previewUrl         — selected but not-yet-uploaded preview
 *   selectedFile       — the File object selected
 *   handleFileSelect   — file input onChange
 *   handleUpload       — saves new profile image
 *   handleCancel       — cancels file selection
 *   getMemberSinceDate — returns member-since Date
 *   handleLogout       — confirm + logout
 *
 * SUB-COMPONENTS:
 *   settingsComponents.jsx — ResetPasswordModal, IdUploader, RestoreConfirmModal
 * ─────────────────────────────────────────────────────────────────────────────
 */

import styles from './SettingsTab.module.css';
import apiService from '../../../services/api';
import { useAuthContext } from '../../../context/AuthContext';
import React, { useState, useRef, useEffect } from 'react';
import { ResetPasswordModal, IdUploader, RestoreConfirmModal } from './settingsComponents';



// ─── Constants ────────────────────────────────────────────────────────────────
const VERDICTS = [
  { value: 'verified',              label: '✅ Account Verified', bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
  { value: 'pending',               label: '⏳ Under Review',     bg: '#eff6ff', color: '#2563eb', border: '#93c5fd' },
  { value: 'pending_reverification',label: '🔄 Re-verification',  bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
  { value: 'partial',               label: '~ Partial',           bg: '#fffbeb', color: '#d97706', border: '#fcd34d' },
  { value: 'rejected',              label: '🚫 Rejected',         bg: '#fdf2f8', color: '#9333ea', border: '#d8b4fe' },
  { value: 'unverified',            label: '✗ Not Verified',      bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
];

// ─── Reset Password Modal ─────────────────────────────────────────────────────


const SettingsTab = ({
  user,
  profileImage,           // current saved profile image URL (from parent / ProfilePage)
  previewUrl,             // local blob preview before upload
  selectedFile,           // File object selected but not yet saved
  handleFileSelect,       // called when user picks a new file — sets previewUrl + selectedFile in parent
  handleUpload,           // legacy upload handler (still called after save for compatibility)
  getMemberSinceDate,
  userProducts,
  onProfileImageSaved,    // ✅ NEW — callback(newUrl) so parent + other sections can update their avatar
}) => {
  const { getToken, user: authUser, updateProfileImage } = useAuthContext();
  const authEmail = authUser?.identifier || authUser?.email ||
    (authUser?.username?.includes('@') ? authUser.username : '') || '';
  const memberSinceDate = getMemberSinceDate?.() || new Date();

  const resolveEmail = (data) =>
    data?.email ||
    data?.identifier ||
    (data?.username && data.username.includes('@') ? data.username : '') ||
    authEmail ||
    '';

  // ── URL resolver ──────────────────────────────────────────────────────────
  const resolveUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    const host = 'https://imselling-marketplace-production.up.railway.app';
    return `${host}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const [form, setForm] = useState({
    first_name:    user?.first_name    || '',
    last_name:     user?.last_name     || '',
    email:         resolveEmail(user) || authEmail,
    phone:         user?.phone_number  || user?.phone || '',
    location:      user?.location      || '',
    business_name: user?.business_name || '',
  });

  const [idFront,     setIdFront]     = useState(user?.id_front  || null);
  const [idBack,      setIdBack]      = useState(user?.id_back   || null);
  const [idFrontFile, setIdFrontFile] = useState(null);
  const [idBackFile,  setIdBackFile]  = useState(null);
  const [businessLogo,     setBusinessLogo]     = useState(user?.business_logo || null);
  const [businessLogoFile, setBusinessLogoFile] = useState(null);

  // ✅ NEW — local profile image state (saved URL from backend, separate from previewUrl)
  const [savedProfileImageUrl, setSavedProfileImageUrl] = useState(
    profileImage || user?.profile_image_url || user?.profile_image || resolveUrl(user?.profile_image) || null
  );
  // ✅ NEW — uploading indicator specifically for profile image
  const [profileImageUploading, setProfileImageUploading] = useState(false);

  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState('');
  const [loading,     setLoading]     = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [editMode,  setEditMode]  = useState(false);
  const [idsSavedOnServer, setIdsSavedOnServer] = useState(!!(user?.id_front && user?.id_back));

  const [verifiedSnapshot, setVerifiedSnapshot] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreMsg, setRestoreMsg] = useState('');

  const [verificationStatus, setVerificationStatus] = useState(
    user?.verification_status || 'unverified'
  );

  // ── Referral participation state ──────────────────────────────────────────
  // referralStatus: null | 'pending' | 'enrolled'
  const [referralStatus,      setReferralStatus]      = useState(user?.referral_status || null);
  const [showReferralModal,   setShowReferralModal]   = useState(false);
  const [referralMpesa,       setReferralMpesa]       = useState('');
  const [referralMpesaConfirm,setReferralMpesaConfirm]= useState('');
  const [referralSaving,      setReferralSaving]      = useState(false);
  const [referralMsg,         setReferralMsg]         = useState('');

  // ── Fetch latest profile from backend on mount ────────────────────────────
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = getToken();
        const data = await apiService.getMyProfile(token);

        setForm({
          first_name:    data.first_name    || '',
          last_name:     data.last_name     || '',
          email:         resolveEmail(data),
          phone:         data.phone_number  || data.phone      || '',
          location:      data.location      || '',
          business_name: data.business_name || '',
        });

        const frontUrl  = data.id_front_url      || resolveUrl(data.id_front);
        const backUrl   = data.id_back_url       || resolveUrl(data.id_back);
        const logoUrl   = data.business_logo_url || resolveUrl(data.business_logo);
        // ── Resolve profile image URL ─────────────────────────────────────────
        // The backend serializer now uses _media_url() so profile_image_url is a
        // clean Supabase URL. We use it directly and add a ?t= cache-buster.
        // Only fall back to path construction if the backend didn't return a URL.
        const SUPA_BASE = 'https://vngtcztqywifguvpszyx.supabase.co/storage/v1/object/public/media';
        const userId = data.id;

        // Use the backend URL if it's clean, otherwise construct the stable path
        const _backendAvatarUrl = data.profile_image_url || data.profile_image || null;
        const _isCleanUrl = _backendAvatarUrl &&
          !_backendAvatarUrl.startsWith('data:') &&
          !_backendAvatarUrl.includes('/media/http'); // guard against any leftover double-URLs in DB

        let avatarUrl = null;
        if (_isCleanUrl) {
          // ✅ Serializer returned a clean URL — add cache-buster and use it
          avatarUrl = `${_backendAvatarUrl}${_backendAvatarUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
        } else if (userId) {
          // Fallback: construct the stable Supabase path (file is always at this location)
          const _rawStored = _backendAvatarUrl || '';
          const _extMatch = _rawStored.match(/profile\.(jpg|jpeg|png|webp|gif)/i);
          const _ext = _extMatch ? _extMatch[1].toLowerCase() : 'jpg';
          avatarUrl = `${SUPA_BASE}/profile_images/users/${userId}/profile.${_ext}?t=${Date.now()}`;
        }

        if (frontUrl)  setIdFront(frontUrl);
        if (backUrl)   setIdBack(backUrl);
        if (logoUrl)   setBusinessLogo(logoUrl);
        if (frontUrl && backUrl) setIdsSavedOnServer(true);

        // ✅ Update saved avatar and notify parent so Header/other sections can use it.
        // Only call onProfileImageSaved on mount if we actually have a URL — prevents
        // overwriting a freshly-uploaded URL with a stale one on re-mount.
        if (avatarUrl) {
          setSavedProfileImageUrl(avatarUrl);
          onProfileImageSaved?.(avatarUrl);
        }

        if (data.verified_snapshot) {
          setVerifiedSnapshot(data.verified_snapshot);
        }
        if (data.verification_status) setVerificationStatus(data.verification_status);
        // ── Referral status: derive from both referral_allowed and referral_status ─
        // referral_allowed (admin toggle) = fully enrolled/approved → show 'enrolled'
        // referral_status = 'pending'     = user submitted, awaiting admin approval
        // Precedence: referral_allowed beats referral_status so an admin-approved
        // user always shows as enrolled regardless of what referral_status contains.
        if (data.referral_allowed) {
          setReferralStatus('enrolled');
        } else if (data.referral_status === 'pending' || data.referral_status === 'enrolled') {
          // DB says enrolled but admin hasn't flipped referral_allowed yet — still pending
          setReferralStatus('pending');
        } else if (data.referral_enrolled !== undefined) {
          setReferralStatus(data.referral_enrolled ? 'pending' : null);
        } else {
          setReferralStatus(null);
        }
        if (data.referral_mpesa) setReferralMpesa(data.referral_mpesa);

        if (data.verification_status === 'pending' || data.verification_status === 'verified') {
          setSubmitted(true);
        }
        if (data.verification_status === 'rejected') {
          setSubmitted(false);
          setEditMode(false);
        }
      } catch (e) {
        console.error('Failed to load profile:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // ── Restore form to verified snapshot ────────────────────────────────────
  const handleRestore = async () => {
    if (!verifiedSnapshot) return;
    setShowRestoreModal(false);
    setSaving(true);

    setForm(prev => ({
      ...prev,
      first_name:    verifiedSnapshot.first_name    || prev.first_name,
      last_name:     verifiedSnapshot.last_name     || prev.last_name,
      phone:         verifiedSnapshot.phone         || prev.phone,
      location:      verifiedSnapshot.location      || prev.location,
      business_name: verifiedSnapshot.business_name || prev.business_name,
    }));

    try {
      const token = getToken();
      const fd = new FormData();
      fd.append('first_name',    verifiedSnapshot.first_name    || '');
      fd.append('last_name',     verifiedSnapshot.last_name     || '');
      fd.append('phone_number',  verifiedSnapshot.phone         || '');
      fd.append('location',      verifiedSnapshot.location      || '');
      fd.append('business_name', verifiedSnapshot.business_name || '');
      await apiService.updateMyProfile(fd, token);
      await apiService.submitForVerification(token);

      setVerificationStatus('pending');
      setSubmitted(true);
      setEditMode(false);
    } catch {
      setRestoreMsg('⚠️ Could not sync to server — profile restored locally. Please save manually.');
      setTimeout(() => setRestoreMsg(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  // ── Referral enrolment handler ────────────────────────────────────────────
  const handleReferralSubmit = async () => {
    const cleaned  = referralMpesa.trim().replace(/\s+/g, '');
    const cleaned2 = referralMpesaConfirm.trim().replace(/\s+/g, '');
    if (!cleaned) { setReferralMsg('❌ Please enter your M-Pesa number.'); return; }
    if (cleaned !== cleaned2) { setReferralMsg('❌ Numbers do not match. Please re-enter carefully.'); return; }
    if (!/^(07\d{8}|2547\d{8}|01\d{8}|\+2547\d{8})$/.test(cleaned)) {
      setReferralMsg('❌ Enter a valid Safaricom number (e.g. 0712345678).');
      return;
    }
    setReferralSaving(true);
    setReferralMsg('');
    try {
      const token = getToken();
      const fd = new FormData();
      // Save referral_status='pending' to DB — admin approval will flip referral_allowed=true
      fd.append('referral_status', 'pending');
      fd.append('referral_mpesa', cleaned);
      await apiService.updateMyProfile(fd, token);
      // Status is now pending admin approval — stored in DB, not locally
      setReferralStatus('pending');
      setReferralMpesa(cleaned);
      setShowReferralModal(false);
      setReferralMpesaConfirm('');
    } catch {
      setReferralMsg('❌ Failed to save. Please try again.');
    } finally {
      setReferralSaving(false);
    }
  };

  const verdictCfg = VERDICTS.find(v => v.value === verificationStatus) || VERDICTS[5];

  // ✅ UPDATED checks — Profile Photo now included and counts toward completeness
  const checks = [
    { label: 'Profile Photo', ok: !!(previewUrl || savedProfileImageUrl) },
    { label: 'First Name',    ok: !!(form.first_name?.trim()) },
    { label: 'Last Name',     ok: !!(form.last_name?.trim())  },
    { label: 'Email',         ok: !!(form.email?.trim())       },
    { label: 'Phone',         ok: !!(form.phone?.trim())       },
    { label: 'Location',      ok: !!(form.location?.trim())    },
    { label: 'ID Front',      ok: !!idFront                    },
    { label: 'ID Back',       ok: !!idBack                     },
  ];
  const completePct = Math.round((checks.filter(c => c.ok).length / checks.length) * 100);
  const barColor = completePct === 100 ? '#16a34a' : completePct >= 50 ? '#f59e0b' : '#ef4444';

  const initials = form.first_name && form.last_name
    ? `${form.first_name[0]}${form.last_name[0]}`.toUpperCase()
    : (user?.username || user?.identifier || 'U').substring(0, 2).toUpperCase();

  // ── Displayed avatar: prefer local preview → saved backend URL → initials ──
  const displayAvatar = previewUrl || savedProfileImageUrl || null;

  // ── Save profile to backend ───────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true); setSaveMsg('');
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append('first_name',    form.first_name);
      fd.append('last_name',     form.last_name);
      fd.append('phone_number',  form.phone);
      fd.append('location',      form.location);
      fd.append('business_name', form.business_name);

      // ✅ If a new profile image file was selected, append it.
      //    The backend will delete the old profile_image and save the new one.
      if (selectedFile) {
        setProfileImageUploading(true);
        fd.append('profile_image', selectedFile);
      }
      if (idFrontFile)      fd.append('id_front',      idFrontFile);
      if (idBackFile)       fd.append('id_back',       idBackFile);
      if (businessLogoFile) fd.append('business_logo', businessLogoFile);

      const updatedProfile = await apiService.updateMyProfile(fd, token);

      // ✅ After save, get the new profile_image URL from the backend response.
      // The serializer now uses _media_url() so profile_image_url is always a clean
      // Supabase URL — no double-URL, no MEDIA_URL prefix contamination.
      // We still add a ?t= cache-buster so the browser re-fetches the new image
      // instead of serving the old one from disk cache.
      if (selectedFile) {
        // Primary: use what the backend returned (serializer is now fixed)
        // Fallback: construct the stable Supabase path we know the file was saved to
        const SUPA = 'https://vngtcztqywifguvpszyx.supabase.co/storage/v1/object/public/media';
        const _uid = updatedProfile?.id || authUser?.id;
        const _savedExt = selectedFile?.name?.split('.').pop()?.toLowerCase() || 'jpg';

        const backendUrl = updatedProfile?.profile_image_url || updatedProfile?.profile_image;
        const baseUrl =
          (backendUrl && !backendUrl.startsWith('data:') && !backendUrl.includes('/media/http'))
            ? backendUrl   // ✅ backend returned a clean URL — use it
            : _uid
              ? `${SUPA}/profile_images/users/${_uid}/profile.${_savedExt}` // stable fallback
              : previewUrl;

        const finalUrl =
          baseUrl && !baseUrl.startsWith('data:')
            ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${Date.now()}`
            : baseUrl;

        setSavedProfileImageUrl(finalUrl);
        // ✅ Call only onProfileImageSaved — ProfilePage wires this to updateProfileImage,
        // so the AuthContext + Header avatar update with a single propagation path.
        // Do NOT also call updateProfileImage() directly here — that causes a double-call
        // and the second one may clobber the first if React batches them differently.
        onProfileImageSaved?.(finalUrl);
        setProfileImageUploading(false);
      }

      if (idFrontFile && idBackFile) setIdsSavedOnServer(true);
      setSaveMsg('✅ Profile saved successfully');
      setTimeout(() => setSaveMsg(''), 3500);

      if (verificationStatus === 'verified') {
        try {
          await apiService.submitForVerification(getToken());
          setVerificationStatus('pending_reverification');
          setSubmitted(true);
          setEditMode(false);
        } catch {
          // Non-fatal
        }
      }
    } catch (e) {
      console.error('Save error:', e);
      setSaveMsg('❌ Failed to save. Please try again.');
      setProfileImageUploading(false);
    } finally {
      setSaving(false);
    }
  };

  const inp = key => ({
    value:     form[key],
    onChange:  e => setForm({ ...form, [key]: e.target.value }),
    className: styles.input,
    onFocus:   e => (e.target.style.borderColor = '#6366f1'),
    onBlur:    e => (e.target.style.borderColor = '#e5e7eb'),
  });

  if (loading) {
    return (
      <div className={styles.wrap}>
        <div className={styles.loadingState}>⏳ Loading your profile…</div>
      </div>
    );
  }

  // ── Post-submission / Under-review / Verified card ────────────────────────
  if (submitted && !editMode) {
    const isVerified       = verificationStatus === 'verified';
    const isRejected       = verificationStatus === 'rejected';
    const isPendingReverif = verificationStatus === 'pending_reverification';

    return (
      <div className={styles.wrap}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Profile Settings</h1>
            <p className={styles.pageSubtitle}>
              {isVerified ? 'Your account is verified.'
                : isRejected ? 'Your verification was not approved.'
                : isPendingReverif ? 'Your updated profile is awaiting re-verification.'
                : 'Your verification request is under review.'}
            </p>
          </div>
          <div className={styles.verificationBadge}
            style={{ background: verdictCfg.bg, color: verdictCfg.color, border: `1.5px solid ${verdictCfg.border}` }}>
            {verdictCfg.label}
          </div>
        </div>

        <div className={styles.card}>
          {/* Status banner */}
          <div className={styles.submittedBanner} style={
            isVerified        ? { background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', borderBottom: '1px solid #bbf7d0' }
            : isRejected      ? { background: 'linear-gradient(135deg,#fdf2f8,#f3e8ff)', borderBottom: '1px solid #d8b4fe' }
            : isPendingReverif? { background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', borderBottom: '1px solid #fdba74' }
            : { background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', borderBottom: '1px solid #93c5fd' }
          }>
            <div className={styles.submittedIconWrap}>
              {isVerified ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#dcfce7" stroke="#16a34a" strokeWidth="1.5"/>
                  <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : isRejected ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#f3e8ff" stroke="#9333ea" strokeWidth="1.5"/>
                  <path d="M9 9l6 6M15 9l-6 6" stroke="#9333ea" strokeWidth="2.2" strokeLinecap="round"/>
                </svg>
              ) : isPendingReverif ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#ffedd5" stroke="#c2410c" strokeWidth="1.5"/>
                  <path d="M12 7v5l3.5 3.5" stroke="#c2410c" strokeWidth="2.2" strokeLinecap="round"/>
                  <path d="M5 12a7 7 0 0 1 7-7" stroke="#c2410c" strokeWidth="2" strokeLinecap="round" strokeDasharray="2 2"/>
                </svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#dbeafe" stroke="#2563eb" strokeWidth="1.5"/>
                  <path d="M12 7v6l3 3" stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round"/>
                </svg>
              )}
            </div>
            <div className={styles.submittedText}>
              <div className={styles.submittedTitle} style={
                isVerified        ? { color: '#15803d' }
                : isRejected      ? { color: '#7e22ce' }
                : isPendingReverif? { color: '#9a3412' }
                : { color: '#1d4ed8' }
              }>
                {isVerified ? '🎉 Account Verified!'
                  : isRejected ? 'Verification Not Approved'
                  : isPendingReverif ? '🔄 Profile Updated — Awaiting Re-verification'
                  : 'Verification Submitted — Under Review'}
              </div>
              <div className={styles.submittedSub} style={
                isVerified        ? { color: '#166534' }
                : isRejected      ? { color: '#6b21a8' }
                : isPendingReverif? { color: '#c2410c' }
                : { color: '#1e40af' }
              }>
                {isVerified
                  ? 'Your account has been verified by our team. You now have full access to all features.'
                  : isRejected
                  ? 'Your verification request was not approved. Please update your details and try again.'
                  : isPendingReverif
                  ? 'You updated your profile after being verified. Our team will review your changes and re-confirm your account.'
                  : "Our team will review your profile and ID documents. You'll be notified once verified."}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Submitted Profile</div>
            <div className={styles.summaryGrid}>
              {[
                { label: 'Name',          value: `${form.first_name} ${form.last_name}`.trim() || '—' },
                { label: 'Email',         value: form.email    || '—' },
                { label: 'Phone',         value: form.phone    || '—' },
                { label: 'Location',      value: form.location || '—' },
                { label: 'Business Name', value: form.business_name || '—' },
              ].map(({ label, value }) => (
                <div key={label} className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{label}</span>
                  <span className={styles.summaryValue}>{value}</span>
                </div>
              ))}
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Profile Photo</span>
                <span className={styles.summaryValue} style={{ color: savedProfileImageUrl ? '#16a34a' : '#dc2626' }}>
                  {savedProfileImageUrl ? '✅ Uploaded' : '⚠️ Not uploaded'}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>ID Documents</span>
                <span className={styles.summaryValue} style={{ color: idFront && idBack ? '#16a34a' : '#dc2626' }}>
                  {idFront && idBack ? '✅ Front & Back uploaded' : '⚠️ Incomplete'}
                </span>
              </div>
            </div>

            <button className={styles.btnEdit} onClick={() => setEditMode(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              {isRejected ? 'Update & Resubmit' : isPendingReverif ? 'Edit Again' : 'Edit Profile'}
            </button>
          </div>

          {/* Referral Programme */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Referral Programme</div>
            <div className={styles.securityCard} style={
              !isVerified ? { opacity: 0.6, pointerEvents: 'none' } : {}
            }>
              <div className={styles.securityLeft}>
                <div className={styles.securityIcon}>🔗</div>
                <div>
                  <div className={styles.securityName}>Participate in Referrals Program and Earn!</div>
                  <div className={styles.securitySub}>
                    {!isVerified
                      ? 'Only available for verified accounts.'
                      : referralStatus === 'enrolled'
                      ? `✅ Enrolled — M-Pesa: ${referralMpesa}`
                      : referralStatus === 'pending'
                      ? `⏳ Pending approval — M-Pesa: ${referralMpesa}`
                      : 'By referring new users to our site through your referral link, you will earn a commission on their premium ads posted.'}
                  </div>
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: referralStatus === 'pending' ? 'default' : isVerified ? 'pointer' : 'not-allowed', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={referralStatus === 'enrolled' || referralStatus === 'pending'}
                  disabled={!isVerified || referralStatus === 'pending'}
                  onChange={() => {
                    if (!isVerified || referralStatus === 'pending') return;
                    if (!referralStatus) {
                      setReferralMpesa('');
                      setReferralMpesaConfirm('');
                      setReferralMsg('');
                      setShowReferralModal(true);
                    } else {
                      setReferralStatus(null);
                    }
                  }}
                  style={{
                    width: 18, height: 18, accentColor: '#6366f1',
                    cursor: referralStatus === 'pending' ? 'default' : isVerified ? 'pointer' : 'not-allowed',
                  }}
                />
                <span style={{
                  fontSize: 13, fontWeight: 700,
                  color: referralStatus === 'enrolled' ? '#16a34a'
                       : referralStatus === 'pending'  ? '#2563eb'
                       : '#6b7280',
                }}>
                  {referralStatus === 'enrolled' ? 'Enrolled'
                   : referralStatus === 'pending' ? 'Pending Approval'
                   : 'Not enrolled'}
                </span>
              </label>
            </div>
            {referralStatus === 'pending' && (
              <div style={{
                marginTop: 8, fontSize: 12, color: '#1d4ed8',
                background: '#eff6ff', border: '1px solid #93c5fd',
                borderRadius: 8, padding: '7px 12px', fontWeight: 600,
              }}>
                ⏳ Your referral enrolment is awaiting admin approval. You'll be notified once it's confirmed.
              </div>
            )}
          </div>

          {/* Account Security */}
          <div className={`${styles.section} ${styles.sectionLast}`}>
            <div className={styles.sectionLabel}>Account Security</div>
            <div className={styles.securityCard}>
              <div className={styles.securityLeft}>
                <div className={styles.securityIcon}>🔑</div>
                <div>
                  <div className={styles.securityName}>Password</div>
                  <div className={styles.securitySub}>Reset your password via a secure email link</div>
                </div>
              </div>
              <button className={styles.btnResetPw} onClick={() => setShowResetModal(true)}>
                Reset Password
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {showResetModal && (
          <ResetPasswordModal userEmail={form.email} onClose={() => setShowResetModal(false)} />
        )}

        {/* Referral M-Pesa Modal */}
        {showReferralModal && (
          <div className={styles.modalBackdrop} onClick={() => setShowReferralModal(false)}>
            <div className={styles.resetModal} onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
              <button className={styles.modalCloseBtn} onClick={() => setShowReferralModal(false)}>×</button>
              <div className={styles.resetIconWrap}>🔗</div>
              <h3 className={styles.resetTitle}>Set Your Cash-Out Account</h3>
              <p className={styles.resetBody}>
                Enter your <strong>Safaricom M-Pesa</strong> number. This will be your
                <strong> cash-out line</strong> for all referral earnings.
                Please double-check before submitting — this is the number we'll send money to.
              </p>
              <div style={{
                background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
                padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e', fontWeight: 600, textAlign: 'left',
              }}>
                ⚠️ Make sure this number is correct. It will be used to disburse your referral earnings via M-Pesa.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, textAlign: 'left' }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                    M-Pesa Number
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g. 0712345678"
                    value={referralMpesa}
                    onChange={e => { setReferralMpesa(e.target.value); setReferralMsg(''); }}
                    className={styles.input}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                    Confirm M-Pesa Number
                  </label>
                  <input
                    type="tel"
                    placeholder="Re-enter your number"
                    value={referralMpesaConfirm}
                    onChange={e => { setReferralMpesaConfirm(e.target.value); setReferralMsg(''); }}
                    className={styles.input}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              {referralMsg && (
                <div style={{
                  fontSize: 12, fontWeight: 600, marginBottom: 12, padding: '7px 11px',
                  borderRadius: 7, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
                }}>
                  {referralMsg}
                </div>
              )}
              <div className={styles.resetActions}>
                <button className={styles.btnCancel} onClick={() => setShowReferralModal(false)}>Cancel</button>
                <button
                  className={styles.btnPrimary}
                  onClick={handleReferralSubmit}
                  disabled={referralSaving}
                >
                  {referralSaving ? <><span className={styles.spinner} /> Saving…</> : '✅ Confirm & Enrol'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Full editable form ────────────────────────────────────────────────────
  const isRejectedEdit = verificationStatus === 'rejected';

  return (
    <div className={styles.wrap}>

      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Profile Settings</h1>
          <p className={styles.pageSubtitle}>
            {isRejectedEdit
              ? 'Your recent changes were rejected. Update your details below and resubmit, or restore your last verified profile.'
              : 'Complete your profile so our team can verify your account.'}
          </p>
        </div>
        <div
          className={styles.verificationBadge}
          style={{ background: verdictCfg.bg, color: verdictCfg.color, border: `1.5px solid ${verdictCfg.border}` }}
        >
          {verdictCfg.label}
        </div>
      </div>

      {/* ✅ REJECTED BANNER */}
      {isRejectedEdit && (
        <div style={{
          background: 'linear-gradient(135deg,#fdf2f8,#f3e8ff)',
          border: '1.5px solid #d8b4fe',
          borderRadius: 12,
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1.5px solid #d8b4fe', fontSize: 18,
            }}>🚫</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#7e22ce', marginBottom: 4 }}>
                Updates Rejected
              </div>
              <div style={{ fontSize: 12, color: '#6b21a8', lineHeight: 1.6 }}>
                The admin reviewed your recent profile changes and did not approve them.
                You can fix the issues and resubmit, or restore your last verified details.
              </div>
              {restoreMsg && (
                <div style={{ fontSize: 12, fontWeight: 600, marginTop: 8,
                  color: restoreMsg.startsWith('✅') ? '#16a34a' : '#d97706' }}>
                  {restoreMsg}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => verifiedSnapshot ? setShowRestoreModal(true) : null}
            title={!verifiedSnapshot ? 'No verified snapshot available yet' : ''}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '9px 18px', borderRadius: 8,
              cursor: verifiedSnapshot ? 'pointer' : 'not-allowed',
              border: '1.5px solid #d8b4fe', background: '#fff',
              color: verifiedSnapshot ? '#7e22ce' : '#c4b5d4',
              fontSize: 13, fontWeight: 700,
              fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap',
              flexShrink: 0, opacity: verifiedSnapshot ? 1 : 0.5,
            }}
            onMouseEnter={e => { if (!verifiedSnapshot) return; e.currentTarget.style.background = '#fdf2f8'; e.currentTarget.style.borderColor = '#9333ea'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#d8b4fe'; }}
          >
            ↩️ Restore Previous Details
            {!verifiedSnapshot && <span style={{ fontSize: 10, fontWeight: 500, marginLeft: 2 }}>(unavailable)</span>}
          </button>
        </div>
      )}

      <div className={styles.card}>

        {/* Card header — avatar with upload */}
        <div className={styles.cardHeader}>
          <div className={styles.avatarWrap}>
            {/* ✅ Show local preview first, fall back to saved backend image, then initials */}
            {displayAvatar ? (
              <img
                src={displayAvatar}
                alt="Profile"
                className={styles.avatarImg}
                style={profileImageUploading ? { opacity: 0.6 } : {}}
              />
            ) : (
              <div className={styles.avatarInitials}>{initials}</div>
            )}
            {/* ✅ Upload spinner overlay when saving profile image */}
            {profileImageUploading && (
              <div style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.55)',
              }}>
                <span className={styles.spinner} style={{ width: 18, height: 18 }} />
              </div>
            )}
            <label htmlFor="st-avatar" className={styles.avatarEditBtn} title="Change photo">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </label>
            <input id="st-avatar" type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
          </div>
          <div className={styles.headerInfo}>
            <div className={styles.headerDisplayName}>
              {form.first_name || form.last_name
                ? `${form.first_name} ${form.last_name}`.trim()
                : user?.username || user?.identifier}
            </div>
            <div className={styles.headerUsername}>{form.email}</div>
            {form.business_name && (
              <div className={styles.headerBusiness}>🏢 {form.business_name}</div>
            )}
            {/* ✅ Hint shown when a new photo is picked but not yet saved */}
            {previewUrl && !profileImageUploading && (
              <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, marginTop: 4 }}>
                📸 New photo selected — click <strong>Save Profile</strong> to upload
              </div>
            )}
          </div>
        </div>

        {/* Profile Completeness — now 8 checks including Profile Photo */}
        <div className={styles.section}>
          <div className={styles.completenessRow}>
            <span className={styles.completenessLabel}>Profile Completeness</span>
            <span className={styles.completenessPct} style={{ color: barColor }}>{completePct}%</span>
          </div>
          <div className={styles.track}>
            <div className={styles.trackFill} style={{ width: `${completePct}%`, background: barColor }} />
          </div>
          <div className={styles.checksGrid}>
            {checks.map(c => (
              <div key={c.label} className={styles.checkItem} style={{ color: c.ok ? '#16a34a' : '#dc2626' }}>
                <span>{c.ok ? '✅' : '✗'}</span>
                <span className={styles.checkName}>{c.label}</span>
                {!c.ok && <span className={styles.checkMissing}>(missing)</span>}
              </div>
            ))}
          </div>
          {completePct < 100 && (
            <p className={styles.completenessHint}>
              Fill in all required fields, upload a profile photo, and upload your ID documents. Business name and logo are optional.
            </p>
          )}
        </div>

        {/* Profile Details */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Profile Details</div>

          <div className={styles.grid2}>
            <div className={styles.fieldWrap}>
              <label className={styles.fieldLabel}>First Name</label>
              <input {...inp('first_name')} type="text" placeholder="First name" />
            </div>
            <div className={styles.fieldWrap}>
              <label className={styles.fieldLabel}>Last Name</label>
              <input {...inp('last_name')} type="text" placeholder="Last name" />
            </div>
          </div>

          <div className={styles.grid2}>
            <div className={styles.fieldWrap}>
              <label className={styles.fieldLabel}>Email</label>
              <input {...inp('email')} type="email" disabled
                className={`${styles.input} ${styles.inputDisabled}`}
                placeholder="Your registered email" />
            </div>
            <div className={styles.fieldWrap}>
              <label className={styles.fieldLabel}>Phone</label>
              <input {...inp('phone')} type="tel" placeholder="+254 7XX XXX XXX" />
            </div>
          </div>

          <div className={styles.grid2}>
            <div className={styles.fieldWrap}>
              <label className={styles.fieldLabel}>Location</label>
              <input {...inp('location')} type="text" placeholder="e.g., Nairobi, Eldoret" />
            </div>
            <div className={styles.fieldWrap}>
              <label className={styles.fieldLabel}>
                Business / Company Name{' '}
                <span className={styles.optionalTag}>(optional)</span>
              </label>
              <input {...inp('business_name')} type="text" placeholder="e.g., My Shop Ltd" />
            </div>
          </div>

          <div className={styles.grid2}>
            <div className={styles.infoTile}>
              <span className={styles.infoTileLabel}>Joined</span>
              <span className={styles.infoTileValue}>
                {memberSinceDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <div className={styles.logoUploaderWrap}>
              <span className={styles.infoTileLabel}>Company / Business Logo <span className={styles.optionalTag}>(optional)</span></span>
              <IdUploader
                label=""
                value={businessLogo}
                onSet={(preview, file) => { setBusinessLogo(preview); setBusinessLogoFile(file); }}
              />
            </div>
          </div>

          <div className={styles.saveRow}>
            <button className={styles.btnSave} onClick={handleSave} disabled={saving}>
              {saving ? <><span className={styles.spinner} /> Saving…</> : '💾 Save Profile'}
            </button>
            {saveMsg && (
              <span className={styles.saveMsg} style={{ color: saveMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>
                {saveMsg}
              </span>
            )}
          </div>
        </div>

        {/* ID Documents */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>ID Documents</div>
          <p className={styles.sectionNote}>
            Upload a government-issued ID to enable full account verification by our team.
          </p>
          <div className={styles.idGrid}>
            <IdUploader label="ID Front" value={idFront} fieldName="id_front"
              onSet={(preview, file) => { setIdFront(preview); setIdFrontFile(file); }} />
            <IdUploader label="ID Back" value={idBack} fieldName="id_back"
              onSet={(preview, file) => { setIdBack(preview); setIdBackFile(file); }} />
          </div>
          {(!idFront || !idBack) && (
            <div className={styles.idWarning}>
              ⚠️ {!idFront && !idBack ? 'Both ID front and back are missing.'
                : !idFront ? 'ID front is missing.' : 'ID back is missing.'}
            </div>
          )}
          <p className={styles.idSaveNote}>
            💡 Click <strong>Save Profile</strong> above to upload your ID documents.
          </p>
        </div>

        {/* Submit for Verification */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Verification Status</div>
          <p className={styles.verdictNote}>
            {isRejectedEdit
              ? completePct === 100 && idFront && idBack
                ? '✅ Profile complete — fix your details above and click below to resubmit for review.'
                : `ℹ️ Profile ${completePct}% complete${!idFront || !idBack ? ', ID documents missing' : ''}. Complete all fields before resubmitting.`
              : completePct === 100 && idFront && idBack && !idsSavedOnServer
              ? '💾 ID documents uploaded but not yet saved — click Submit below and they will be saved automatically.'
              : completePct === 100 && idFront && idBack
              ? '✅ Profile complete and ID uploaded — click below to submit for admin review.'
              : `ℹ️ Profile ${completePct}% complete${!idFront || !idBack ? ', ID documents missing' : ''}. Complete all fields before submitting.`}
          </p>
          <button
            className={styles.btnSubmitVerification}
            disabled={completePct < 100 || !idFront || !idBack}
            onClick={async () => {
              try {
                if (idFrontFile || idBackFile || selectedFile || businessLogoFile) {
                  setSaving(true);
                  const token = getToken();
                  const fd = new FormData();
                  fd.append('first_name',    form.first_name);
                  fd.append('last_name',     form.last_name);
                  fd.append('phone_number',  form.phone);
                  fd.append('location',      form.location);
                  fd.append('business_name', form.business_name);
                  if (selectedFile)     fd.append('profile_image', selectedFile);
                  if (idFrontFile)      fd.append('id_front',      idFrontFile);
                  if (idBackFile)       fd.append('id_back',       idBackFile);
                  if (businessLogoFile) fd.append('business_logo', businessLogoFile);
                  const updatedProfile = await apiService.updateMyProfile(fd, token);

                  // ✅ Propagate saved profile image URL after submit-upload.
                  // Backend serializer now returns a clean profile_image_url — use it
                  // directly. Add cache-buster so browser fetches the new file.
                  if (selectedFile) {
                    const SUPA = 'https://vngtcztqywifguvpszyx.supabase.co/storage/v1/object/public/media';
                    const _uid = updatedProfile?.id || authUser?.id;
                    const _ext = selectedFile?.name?.split('.').pop()?.toLowerCase() || 'jpg';
                    const _backendUrl = updatedProfile?.profile_image_url || updatedProfile?.profile_image;
                    const _isClean = _backendUrl && !_backendUrl.startsWith('data:') && !_backendUrl.includes('/media/http');
                    const _base = _isClean ? _backendUrl
                      : _uid ? `${SUPA}/profile_images/users/${_uid}/profile.${_ext}` : previewUrl;
                    const newAvatarUrl = _base && !_base.startsWith('data:')
                      ? `${_base}${_base.includes('?') ? '&' : '?'}t=${Date.now()}`
                      : _base;
                    setSavedProfileImageUrl(newAvatarUrl);
                    onProfileImageSaved?.(newAvatarUrl);
                  }
                  setSaving(false);
                }
                await apiService.submitForVerification(getToken());
                setVerificationStatus('pending');
                setSubmitted(true);
                setEditMode(false);
              } catch (e) {
                setSaving(false);
                alert(e?.message || 'Failed to submit. Please try again.');
              }
            }}
          >
            {saving
              ? <><span className={styles.spinner} /> Saving & Submitting…</>
              : isRejectedEdit ? '🔄 Resubmit for Verification' : '🛡️ Submit for Verification'}
          </button>
        </div>

        {/* Referral Programme */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Referral Programme</div>
          <div className={styles.securityCard} style={
            verificationStatus !== 'verified' ? { opacity: 0.6, pointerEvents: 'none' } : {}
          }>
            <div className={styles.securityLeft}>
              <div className={styles.securityIcon}>🔗</div>
              <div>
                <div className={styles.securityName}>Participate in Referrals Program and Earn!</div>
                <div className={styles.securitySub}>
                  {verificationStatus !== 'verified'
                    ? 'Only available for verified accounts.'
                    : referralStatus === 'enrolled'
                    ? `✅ Enrolled — M-Pesa: ${referralMpesa}`
                    : referralStatus === 'pending'
                    ? `⏳ Pending approval — M-Pesa: ${referralMpesa}`
                    : 'By referring new users to our site through your referral link, you will earn a commission on their premium ads posted.'}
                </div>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: referralStatus === 'pending' ? 'default' : verificationStatus === 'verified' ? 'pointer' : 'not-allowed', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={referralStatus === 'enrolled' || referralStatus === 'pending'}
                disabled={verificationStatus !== 'verified' || referralStatus === 'pending'}
                onChange={() => {
                  if (verificationStatus !== 'verified' || referralStatus === 'pending') return;
                  if (!referralStatus) {
                    setReferralMpesa('');
                    setReferralMpesaConfirm('');
                    setReferralMsg('');
                    setShowReferralModal(true);
                  } else {
                    setReferralStatus(null);
                  }
                }}
                style={{
                  width: 18, height: 18, accentColor: '#6366f1',
                  cursor: referralStatus === 'pending' ? 'default' : verificationStatus === 'verified' ? 'pointer' : 'not-allowed',
                }}
              />
              <span style={{
                fontSize: 13, fontWeight: 700,
                color: referralStatus === 'enrolled' ? '#16a34a'
                     : referralStatus === 'pending'  ? '#2563eb'
                     : '#6b7280',
              }}>
                {referralStatus === 'enrolled' ? 'Enrolled'
                 : referralStatus === 'pending' ? 'Pending Approval'
                 : 'Not enrolled'}
              </span>
            </label>
          </div>
          {referralStatus === 'pending' && (
            <div style={{
              marginTop: 8, fontSize: 12, color: '#1d4ed8',
              background: '#eff6ff', border: '1px solid #93c5fd',
              borderRadius: 8, padding: '7px 12px', fontWeight: 600,
            }}>
              ⏳ Your referral enrolment is awaiting admin approval. You'll be notified once it's confirmed.
            </div>
          )}
        </div>

        {/* Account Security */}
        <div className={`${styles.section} ${styles.sectionLast}`}>
          <div className={styles.sectionLabel}>Account Security</div>
          <div className={styles.securityCard}>
            <div className={styles.securityLeft}>
              <div className={styles.securityIcon}>🔑</div>
              <div>
                <div className={styles.securityName}>Password</div>
                <div className={styles.securitySub}>Reset your password via a secure email link</div>
              </div>
            </div>
            <button className={styles.btnResetPw} onClick={() => setShowResetModal(true)}>
              Reset Password
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

      </div>

      {showResetModal && (
        <ResetPasswordModal userEmail={form.email} onClose={() => setShowResetModal(false)} />
      )}

      {showRestoreModal && verifiedSnapshot && (
        <RestoreConfirmModal
          snapshot={verifiedSnapshot}
          onConfirm={handleRestore}
          onClose={() => setShowRestoreModal(false)}
        />
      )}

      {/* Referral M-Pesa Modal */}
      {showReferralModal && (
        <div className={styles.modalBackdrop} onClick={() => setShowReferralModal(false)}>
          <div className={styles.resetModal} onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <button className={styles.modalCloseBtn} onClick={() => setShowReferralModal(false)}>×</button>
            <div className={styles.resetIconWrap}>🔗</div>
            <h3 className={styles.resetTitle}>Set Your Cash-Out Account</h3>
            <p className={styles.resetBody}>
              Enter your <strong>Safaricom M-Pesa</strong> number. This will be your
              <strong> cash-out line</strong> for all referral earnings.
              Please double-check before submitting — this is the number we'll send money to.
            </p>
            <div style={{
              background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e', fontWeight: 600, textAlign: 'left',
            }}>
              ⚠️ Make sure this number is correct. It will be used to disburse your referral earnings via M-Pesa.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, textAlign: 'left' }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  M-Pesa Number
                </label>
                <input
                  type="tel"
                  placeholder="e.g. 0712345678"
                  value={referralMpesa}
                  onChange={e => { setReferralMpesa(e.target.value); setReferralMsg(''); }}
                  className={styles.input}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  Confirm M-Pesa Number
                </label>
                <input
                  type="tel"
                  placeholder="Re-enter your number"
                  value={referralMpesaConfirm}
                  onChange={e => { setReferralMpesaConfirm(e.target.value); setReferralMsg(''); }}
                  className={styles.input}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            {referralMsg && (
              <div style={{
                fontSize: 12, fontWeight: 600, marginBottom: 12, padding: '7px 11px',
                borderRadius: 7, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
              }}>
                {referralMsg}
              </div>
            )}
            <div className={styles.resetActions}>
              <button className={styles.btnCancel} onClick={() => setShowReferralModal(false)}>Cancel</button>
              <button
                className={styles.btnPrimary}
                onClick={handleReferralSubmit}
                disabled={referralSaving}
              >
                {referralSaving ? <><span className={styles.spinner} /> Saving…</> : '✅ Confirm & Enrol'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsTab;












